import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { ApiError } from "../../lib/api";
import { banners, type BannerPreset } from "../profile/banner";
import { rooms, type Room } from "../rooms/api";
import { isListeningTo, startListening } from "../rooms/listening";
import { studio } from "./api";
import type { Scene } from "./compositor";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_LAYOUT,
  LAYOUTS,
  layoutById,
} from "./layouts";
import { setMonitor } from "./audio";
import { useStudio } from "./useStudio";

/**
 * The browser studio, in a column of its own.
 *
 * ## Why it is in the console and not on a page of its own
 *
 * It was a page, briefly, and that was wrong. Hosting is not one task: it is
 * watching the queue, reading chat, reviewing what came in and skipping a track
 * that is not landing - all while on air. A studio on a separate page means
 * leaving the room to change a camera, which is the moment nobody is watching
 * the queue.
 *
 * ## And why it gets a whole column
 *
 * It briefly shared the deck's column, with its settings folded into a
 * disclosure to make room. That hid the half of the studio a host most wants to
 * play with - the layout, the background, which camera - behind a click, to fit
 * beside controls it has nothing to do with.
 *
 * The console now spends a column on the studio only when the studio is open,
 * and nothing on it when it is closed. So there is no shortage to design around
 * and every control is simply visible.
 *
 * ## Nothing here may unmount mid-broadcast
 *
 * The render loop, the devices and the peer connection all live in `useStudio`,
 * so this component disappearing takes the broadcast with it. That is why the
 * console opens it from a local toggle rather than from `room.source`: the room
 * is polled, and a single response that arrived while the status was rolling
 * over would otherwise drop a live show.
 */
export function StudioColumn({
  room,
  onChange,
  onBroadcasting,
}: {
  room: Room;
  onChange: () => void;
  /** So the console can stand its own Monitor button down. See below. */
  onBroadcasting: (live: boolean) => void;
}) {
  const [layoutId, setLayoutId] = useState(DEFAULT_LAYOUT.id);
  const [showCard, setShowCard] = useState(true);
  const [showBrand, setShowBrand] = useState(true);
  const [background, setBackground] = useState<BannerPreset | null>(null);
  const [deckLevel, setDeckLevel] = useState(0.8);
  const [micLevel, setMicLevel] = useState(1);
  const [camera, setCamera] = useState("");
  const [microphone, setMicrophone] = useState("");
  const [monitoring, setMonitoring] = useState(true);
  /** How many consecutive seconds the outgoing mix has been silent. */
  const [silence, setSilence] = useState(0);

  const presets = useQuery({
    queryKey: ["banner-presets"],
    queryFn: banners.presets,
  });

  const credentials = useQuery({
    queryKey: ["studio-credentials"],
    queryFn: studio.credentials,
    // The key is write access to this broadcast. Nothing about it should sit in
    // a cache waiting for a shoulder to look over.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const scene: Scene = useMemo(
    () => ({
      layout: layoutById(layoutId),
      background: background?.stops ?? ["#0d0d10", "#1a1a20"],
      backgroundAngle: background?.angle ?? 160,
      camera: null,
      showCamera: true,
      track: room.now_playing
        ? {
            title: room.now_playing.title,
            artist: room.now_playing.artist.display_name,
            artwork: room.now_playing.artwork ?? room.now_playing.artwork_small,
          }
        : null,
      showCard,
      brand: {
        username: room.host.username,
        displayName: room.host.display_name,
        avatar: room.host.avatar,
      },
      showBrand,
      live: room.status === "live",
    }),
    [layoutId, background, room, showCard, showBrand],
  );

  const deck = useStudio(scene, true);

  // The mix does not exist until the broadcast starts, so the levels are
  // applied on change *and* re-applied here - otherwise a host who set them
  // before going live would find them ignored.
  useEffect(() => deck.setDeckLevel(deckLevel), [deck, deckLevel]);
  useEffect(() => deck.setMicLevel(micLevel), [deck, micLevel]);

  /*
   * While broadcasting, the host is in their own room whether they like it or
   * not.
   *
   * `RoomAudio` - which is what actually loads and plays the track - only
   * exists while somebody is tuned. So a host who had not pressed Monitor was
   * broadcasting a mix with no music in it, and could not tell, because the
   * only way to hear the problem is to be monitoring.
   *
   * Tuning is therefore not the host's choice during a broadcast; whether they
   * *hear* it is, and that is the toggle below.
   */
  useEffect(() => {
    if (deck.status !== "live") return;
    if (!isListeningTo(room.username)) startListening(room);
  }, [deck.status, room]);

  useEffect(() => {
    onBroadcasting(deck.status === "live");
  }, [deck.status, onBroadcasting]);

  // Re-applied when a broadcast starts as well as when it is toggled: the gain
  // node does not exist until the deck is tapped, and the deck is tapped when
  // the broadcast begins.
  useEffect(() => setMonitor(monitoring), [monitoring, deck.status]);

  /*
   * Silence, counted rather than assumed.
   *
   * A broadcast carrying no audio looks exactly like a working one from behind
   * the desk: the connection is up, the preview moves, the timer counts. The
   * only place the difference shows is a meter that never leaves zero, and a
   * host mid-set is not staring at a meter.
   *
   * So it is counted and said out loud. Eight seconds, because the first few
   * after starting are legitimately quiet - the deck has to fetch a manifest
   * and fill a buffer before a single sample exists - and warning during that
   * would train people to ignore it.
   */
  useEffect(() => {
    if (deck.status !== "live") {
      setSilence(0);

      return;
    }

    const timer = setInterval(() => {
      setSilence((seconds) => (deck.level > 0.001 ? 0 : seconds + 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [deck.status, deck.level]);

  const goLive = useMutation({
    // `'stream'` on the way in, so the room is in the mode the ingest server
    // requires before the host can press start.
    mutationFn: () => rooms.goLive("stream"),
    onSuccess: onChange,
  });

  const ready = room.status === "live" && room.source === "stream";
  const missing =
    credentials.error instanceof ApiError && credentials.error.status === 404;

  return (
    <aside
      className="flex min-w-0 flex-col gap-6 bg-app p-6 lg:min-h-0 lg:overflow-y-auto"
      data-studio
    >
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">Studio</span>
        <span
          className="font-mono text-[11px] text-faint"
          data-studio-connection
        >
          {deck.status === "live" ? deck.connection : "off air"}
        </span>
      </div>

      {/*
        The preview and the encoder's canvas.

        The second is 720p and never displayed - `hidden` rather than
        unmounted, because `captureStream` needs a canvas that is in the
        document and an unmounted one produces a stream of nothing.
      */}
      {/*
        `shrink-0`, or there is no preview.

        This is a flex column that scrolls, and the column got much taller when
        the studio gained its own controls. Flex children shrink by default, and
        an `aspect-video` box has a min-content height of nothing - so the
        preview quietly collapsed to a 1px line while the canvas kept its
        1280x720 backing store and carried on compositing perfectly. The stream
        was fine; the host just could not see it.
      */}
      <div className="shrink-0 overflow-hidden rounded-[var(--radius-panel)] border border-line bg-black">
        <canvas
          ref={deck.previewRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="block aspect-video w-full"
          data-studio-preview
        />
      </div>

      <canvas
        ref={deck.outputRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="hidden"
        aria-hidden
        data-studio-output
      />

      {missing && (
        <p className="rounded-[var(--radius-control)] border border-line px-3 py-2.5 font-mono text-[11px] leading-relaxed text-ghost">
          the studio is not switched on for this platform yet
        </p>
      )}

      {deck.error && (
        <p
          role="alert"
          data-studio-error
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          {deck.error}
        </p>
      )}

      {deck.status === "live" && <Meter level={deck.level} />}

      {silence >= 8 && (
        <p
          data-studio-silent
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          nothing is going out — the mix has been silent for {silence}s. check
          the deck is playing and the music level is up.
        </p>
      )}

      {ready ? (
        <button
          type="button"
          disabled={!credentials.data || deck.status === "starting"}
          onClick={() =>
            deck.status === "live"
              ? void deck.stop()
              : void deck.start(credentials.data!.whip)
          }
          data-studio-toggle
          className={`rounded-[var(--radius-control)] py-3 text-[13px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            deck.status === "live"
              ? "bg-hot text-white hover:opacity-90"
              : "bg-accent text-accent-ink hover:bg-accent-hover"
          }`}
        >
          {deck.status === "live"
            ? "Stop broadcasting"
            : deck.status === "starting"
              ? "Connecting…"
              : "Start broadcasting"}
        </button>
      ) : (
        /*
          The room has to be on air in stream mode before the ingest server will
          take a publisher. Rather than explain that, this is the button that
          does it - and it is the only one offered until it has been pressed, so
          the order cannot be got wrong.
        */
        <button
          type="button"
          disabled={goLive.isPending || missing}
          onClick={() => goLive.mutate()}
          data-studio-golive
          className="rounded-[var(--radius-control)] bg-accent py-3 text-[13px] font-bold text-accent-ink transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {room.status === "live"
            ? "Switch this room to the studio"
            : "Go on air in studio mode"}
        </button>
      )}

      {room.status === "live" && room.source !== "stream" && (
        <p className="font-mono text-[10px] leading-relaxed text-ghost">
          this room is on air from the catalogue — switching restarts the
          broadcast in studio mode
        </p>
      )}

      {/* ------------------------------------------------------------------ */}

      <Group label="Camera">
        <div className="flex flex-col gap-2.5">
          <Toggle
            on={deck.cameraOn}
            onClick={() =>
              deck.cameraOn
                ? deck.stopCamera()
                : void deck.startCamera(camera || undefined)
            }
            data-studio-camera
          >
            {deck.cameraOn ? "Camera on" : "Enable camera"}
          </Toggle>

          {/*
            Labels are empty until permission has been granted once, so before
            that this would be a list of blank entries. Offered only when there
            is genuinely a choice to make.
          */}
          {deck.devices.cameras.length > 1 && (
            <Picker
              label="Which camera"
              value={camera}
              options={deck.devices.cameras}
              onChange={(id) => {
                setCamera(id);
                // Switched live, because a host discovering they are on the
                // wrong camera wants it fixed now, not after a restart.
                if (deck.cameraOn) void deck.startCamera(id || undefined);
              }}
            />
          )}
        </div>
      </Group>

      <Group label="Microphone">
        <div className="flex flex-col gap-2.5">
          <Toggle
            on={deck.micOn}
            onClick={() =>
              deck.micOn
                ? deck.stopMic()
                : void deck.startMic(microphone || undefined)
            }
            data-studio-mic
          >
            {deck.micOn ? "Microphone on" : "Enable microphone"}
          </Toggle>

          {deck.devices.microphones.length > 1 && (
            <Picker
              label="Which microphone"
              value={microphone}
              options={deck.devices.microphones}
              onChange={(id) => {
                setMicrophone(id);
                if (deck.micOn) void deck.startMic(id || undefined);
              }}
            />
          )}
        </div>
      </Group>

      <Group label="Layout">
        <div className="grid grid-cols-2 gap-2">
          {LAYOUTS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setLayoutId(option.id)}
              aria-pressed={layoutId === option.id}
              className={`rounded-[var(--radius-control)] border px-3 py-2.5 text-left text-[12px] transition-colors ${
                layoutId === option.id
                  ? "border-accent bg-accent-wash text-ink"
                  : "border-line text-soft hover:border-line-lit hover:text-ink"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="mt-2 font-mono text-[10px] leading-relaxed text-ghost">
          {layoutById(layoutId).hint}
        </p>
      </Group>

      <Group label="On screen">
        <Check label="Now playing" on={showCard} onChange={setShowCard} />
        <Check label="Your name" on={showBrand} onChange={setShowBrand} />
      </Group>

      <Group label="Background">
        <div className="flex flex-wrap gap-2">
          {(presets.data?.presets ?? []).map((preset) => (
            <button
              key={preset.preset}
              type="button"
              onClick={() => setBackground(preset)}
              aria-pressed={background?.preset === preset.preset}
              aria-label={preset.label}
              title={preset.label}
              style={{
                background: `linear-gradient(${preset.angle}deg, ${preset.stops.join(", ")})`,
              }}
              className={`h-8 w-8 rounded-full border-2 transition-colors ${
                background?.preset === preset.preset
                  ? "border-ink"
                  : "border-transparent"
              }`}
            />
          ))}
        </div>
        <p className="mt-2 font-mono text-[10px] leading-relaxed text-ghost">
          the same eight your profile header uses
        </p>
      </Group>

      <Group label="Mix">
        <Slider label="Music" value={deckLevel} onChange={setDeckLevel} />
        <Slider label="Microphone" value={micLevel} onChange={setMicLevel} />
        <p className="mt-1 font-mono text-[10px] leading-relaxed text-ghost">
          the balance of what goes out — what you hear does not change it
        </p>
      </Group>

      <Group label="Monitor">
        <Check label="Hear the deck" on={monitoring} onChange={setMonitoring} />
        <p className="mt-1 font-mono text-[10px] leading-relaxed text-ghost">
          your speakers only — the music keeps going out either way, so you can
          talk over it without hearing yourself
        </p>
      </Group>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */

function Toggle({
  on,
  children,
  ...rest
}: {
  on: boolean;
  children: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={on}
      {...rest}
      className={`rounded-[var(--radius-control)] border py-2.5 text-[12px] font-bold transition-colors ${
        on
          ? "border-transparent bg-raised text-ink"
          : "border-line text-soft hover:border-line-strong hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function Picker({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: MediaDeviceInfo[];
  onChange: (id: string) => void;
}) {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="w-full rounded-[var(--radius-control)] border border-line bg-app px-2.5 py-2 font-mono text-[11px] text-soft"
      >
        <option value="">System default</option>
        {options.map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label || "Unnamed device"}
          </option>
        ))}
      </select>
    </label>
  );
}

function Group({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="meta-caps mb-2.5 text-ghost">{label}</p>
      {children}
    </div>
  );
}

function Check({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between py-1.5 text-[13px] text-ink">
      {label}
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[var(--color-accent)]"
      />
    </label>
  );
}

function Slider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="block py-1 text-[13px] text-ink">
      <span className="mb-1 flex items-center justify-between">
        {label}
        <span className="font-mono text-[10px] text-faint">
          {Math.round(value * 100)}
        </span>
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--color-accent)]"
      />
    </label>
  );
}

/**
 * What is going out, right now.
 *
 * The connection state lives in the column header rather than here, because the
 * two answer the same question - "is anything reaching anybody?" - and a moving
 * meter on a failed connection is a host talking to nobody.
 */
function Meter({ level }: { level: number }) {
  const width = Math.min(100, Math.round(level * 140));

  return (
    <div className="flex items-center gap-3" data-studio-meter>
      <span className="meta-caps text-ghost">out</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-raised">
        <div
          className={`h-full transition-[width] duration-100 ${width > 92 ? "bg-hot" : "bg-fresh"}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}
